// AI-CONTEXT: See docs/features/arbeidsvaardigheden.md
import { EFFECTEN } from './arbeidsvaardighedenStandaard'
import { oudsteEerst } from './beoordelingDocument'

/**
 * Groei op de negen thema's van MSI Arbeidsvaardigheden.
 *
 * Groei is de laatste beoordeling min de eerste, per thema. Niet de laatste min
 * de vorige: de begeleider beoordeelt elk kwartaal, en wat iemand in een jaar
 * heeft geleerd is de afstand tot het beginpunt, niet de laatste schommeling.
 *
 * Er wordt op `Datum` gesorteerd en niet op `Timestamp`. De datum is de meetdag,
 * de timestamp het moment van opslaan; een beoordeling die een week later wordt
 * ingevoerd hoort op zijn eigen meetdag te blijven staan.
 *
 * Er wordt gerekend met `Scores`, het veld dat bij elke beoordeling is
 * meegeschreven, en niet met de antwoorden. Een latere versie van de standaard
 * mag een oude meting niet met terugwerkende kracht anders scoren
 * (besluit 6 in docs/features/arbeidsvaardigheden.md).
 *
 * Een thema zonder score in een beoordeling telt daar niet als meting: een half
 * ingevuld thema heeft `Scores[effectId] === null` en is geen meetpunt.
 *
 * Dit is de frontend-kant van dezelfde rekenregel als `growthForPersona` in
 * `functions/workSkillsHelpers/buildWorkSkillsBenchmark.js` (de cross-tenant
 * benchmark). Wijzigt de regel, wijzig beide.
 */

// AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
/**
 * Beoordelingen met een bruikbare score op dit thema, oudste eerst.
 *
 * De volgorde komt uit `oudsteEerst`, dezelfde regel die het tabblad gebruikt.
 * Op alleen `Datum` sorteren volstaat niet: dat veld heeft dagprecisie, dus twee
 * beoordelingen op dezelfde dag eindigen gelijk en dan zou de volgorde van
 * Firestore bepalen welke de nulmeting is - en daarmee of er groei of krimp
 * uitkomt.
 */
const metingenVoor = (beoordelingen, effectId) =>
    (beoordelingen || [])
        .filter(b => Number.isFinite(b?.Scores?.[effectId]))
        .sort(oudsteEerst)

/**
 * Groei per thema voor één deelnemer.
 *
 * @param {Array<object>} beoordelingen - WorkSkillsAssessments van één deelnemer
 * @returns {Array<{id: string, naam: string, eerste: number|null, laatste: number|null,
 *   groei: number|null, eersteDatum: string|null, laatsteDatum: string|null, metingen: number}>}
 *   Eén rij per thema, in de volgorde van de standaard. `groei` is null zolang er
 *   geen twee metingen zijn - niet 0, want dat leest als "geen verandering".
 */
const groeiPerEffect = (beoordelingen = []) =>
    EFFECTEN.map(effect => {
        const metingen = metingenVoor(beoordelingen, effect.id)
        if (metingen.length < 2) {
            return {
                id: effect.id,
                naam: effect.naam,
                eerste: metingen[0]?.Scores?.[effect.id] ?? null,
                laatste: null,
                groei: null,
                eersteDatum: metingen[0]?.Datum || null,
                laatsteDatum: null,
                metingen: metingen.length,
            }
        }

        const eersteMeting = metingen[0]
        const laatsteMeting = metingen[metingen.length - 1]
        const eerste = eersteMeting.Scores[effect.id]
        const laatste = laatsteMeting.Scores[effect.id]

        return {
            id: effect.id,
            naam: effect.naam,
            eerste,
            laatste,
            groei: laatste - eerste,
            eersteDatum: eersteMeting.Datum || null,
            laatsteDatum: laatsteMeting.Datum || null,
            metingen: metingen.length,
        }
    })

export { groeiPerEffect }
export default groeiPerEffect
